/**
 * Ответ на пробу задержки (задача 1.11).
 *
 * Зачем вообще: путь «модератор нажал → зритель увидел» проходит через
 * сеть, hub, CEF и композитор OBS. Оценить его на глаз нельзя,
 * а контрольная точка блока 0 — уложиться в 100 мс.
 *
 * Почему оверлей не отправляет свою метку времени. Панель и оверлей
 * живут на разных машинах, их часы расходятся, и разность
 * «метка оверлея минус метка панели» измеряла бы расхождение вместе
 * с задержкой. Расхождение легко превышает те самые 100 мс. Поэтому
 * оверлей возвращает только идентификатор пробы и СВОЁ время
 * отрисовки, измеренное целиком по своим часам. Ни одно сравнение
 * не пересекает границу машины.
 */

/** Что оверлей отправляет в ответ на пробу. */
import { decodeMessage } from '@owm/scene';

export interface ProbeAck {
  readonly t: 'ack';
  readonly probeId: string;
  /** Время от получения сообщения до кадра на экране, миллисекунды. */
  readonly renderMs: number;
}

/** Идентификатор пробы ограничен так же, как на стороне hub. */
const MAX_PROBE_ID = 64;

/**
 * Достаёт идентификатор пробы, если сообщение ею является.
 *
 * Возвращает null для всего остального: оверлей получает поток чужих
 * сообщений и обязан молча пропускать незнакомое.
 */
export function probeIdOf(raw: unknown): string | null {
  const parsed = decodeMessage(raw);
  if (parsed === null) return null;
  const message = parsed as Record<string, unknown>;

  if (message.t !== 'trigger' || message.kind !== 'debug.ping') return null;

  const payload = message.payload;
  if (typeof payload !== 'object' || payload === null) return null;

  const probeId = (payload as Record<string, unknown>).probeId;
  if (typeof probeId !== 'string' || probeId === '' || probeId.length > MAX_PROBE_ID) {
    return null;
  }

  return probeId;
}

/** Собирает ответ. Отдельно от отправки, чтобы проверять без сокета. */
export function buildAck(probeId: string, renderMs: number): ProbeAck {
  return {
    t: 'ack',
    probeId,
    // Округление до сотых: субмикросекундная точность здесь ничего
    // не значит, а в JSON занимает место на каждой пробе
    renderMs: Math.max(0, Math.round(renderMs * 100) / 100),
  };
}

/**
 * Что нужно от окружения, чтобы измерить отрисовку.
 *
 * Объявлено интерфейсом, а не взято из window: тест подставляет
 * управляемые часы и кадры, иначе проверка зависела бы от того,
 * когда браузеру вздумается отрисовать кадр.
 */
export interface ProbeEnvironment {
  now: () => number;
  /** Вызывает переданное после ближайшего кадра. */
  afterFrame: (fn: () => void) => void;
  send: (message: ProbeAck) => void;
}

/**
 * Отвечает на пробу, измерив время до кадра.
 *
 * Ответ отправляется ПОСЛЕ отрисовки, а не сразу по получении. Иначе
 * измерялась бы доставка сообщения, а не появление картинки — а зритель
 * ждёт именно картинку. Разница между этими двумя величинами и есть
 * то, ради чего замер затевался.
 */
export function answerProbe(probeId: string, env: ProbeEnvironment): void {
  const receivedAt = env.now();

  env.afterFrame(() => {
    env.send(buildAck(probeId, env.now() - receivedAt));
  });
}
